import React, { useContext, useEffect } from 'react';
import { orderBy } from 'lodash';
import { connect } from 'react-redux';
import { getPriorStudentsSubdir } from 'legacy-src/utility/utils';
import ResultsWrapper from 'legacy-src/components/assessment/Results/ResultsWrapper';
import { ResultsContext } from './ResultsDataProvider';
import { BreadcrumbContext } from 'chameleon/ui-stack/ui/basic/breadcrumbs';
import { getPerformanceStacks } from 'legacy-src/components/assessment/Results/utility';
import HorizontalTabs from 'chameleon/ui-stack/ui/base/tabs/horizontal-tabs';
import StudentResultsHeader from './StudentResultsHeader';
import { Feedback } from 'legacy-src/components/assessment/Results/AggregateResults/Student/Feedback';
import { withRouter } from 'react-router';
import { compose } from 'redux';

const TeacherStudentResultsContainer = (props) => {
  const resultsData = useContext(ResultsContext);
  const { dispatch, ACTIONS } = useContext(BreadcrumbContext);
  const {
    assignment,
    params,
    routes,
    router,
    students = [],
    courseOutlineConfig,
    isPPC,
    isQB,
    learnosityReportsData,
    hasFrqQuestions,
    location: { pathname },
  } = resultsData;

  const {
    children,
    showFrqResponses,
    masteryReportFlag,
    questions,
    tagPairsData: {
      assignmentProgressWithTagPairs: {
        assessmentShortName: currentPartShortName,
        otherParts,
      },
    },
  } = props;

  const { subject, studentId, ppcPartId, questionId, partId } = params;
  let student = students.find((s) => s.id === studentId);
  if (!student) {
    student = assignment.students.find((s) => s.id === studentId);
  }

  const { id: assignmentId } = assignment;

  if (!student) {
    return <div>No student results found</div>;
  }

  let tabs = [];
  let questionTabs = [];
  /**
   * When looking at a PPC Topic and Skill report, build the ResultsMenu tabs with;
   * - a constant "Topic and SKills" tab and
   * - Question tabs for the different assignment parts that have been assigned
   */
  if (!isQB && isPPC) {
    questionTabs = [
      {
        key: `questions-${assignmentId}`,
        id: Number.parseInt(assignmentId, 10),
        label: `Questions${
          currentPartShortName ? ` - ${currentPartShortName}` : ''
        }`,
        name: 'questions',
      },
    ];

    questionTabs = questionTabs.concat(
      otherParts
        .filter(({ status }) => !!status)
        .map((otherPart) => {
          const { shortName, assignmentId: partId } = otherPart;
          const label = `Questions - ${shortName}`;

          return {
            key: `questions-${partId}`,
            id: partId,
            label,
          };
        }),
    );

    questionTabs = orderBy(questionTabs, ['label']);

    const { curriculumCheckTabLabel } = (courseOutlineConfig || {}).data || {};
    tabs.push({
      key: 'topics-and-skills',
      label: `${curriculumCheckTabLabel}${
        questionTabs.length > 1 ? ' - All Parts' : ''
      }`,
      name: 'resources',
    });

    tabs = tabs.concat(questionTabs);
  }

  /**
   * https://academicmerit.atlassian.net/browse/FY-9946
   * questionTabs length greater than 1 means we are displaying more than one part
   *  and that's when we should hide the time and performance bar
   */
  const isPPCMCQWithMultipleParts = isPPC && questionTabs.length > 1;
  const showPerformanceAndTime = !isPPCMCQWithMultipleParts;

  const studentContextPath = `/${subject}/assessments/results/${
    params.assignmentId
  }/${getPriorStudentsSubdir(routes)}students/${studentId}/`;
  const resultsPath = `${studentContextPath}resources`;
  const questionsPath = `${studentContextPath}${isPPC ? 'parts/:partId/' : ''}`;

  const studentScore = getPerformanceStacks(learnosityReportsData, studentId);

  let isStudentResponseAvailable;
  if (showFrqResponses && hasFrqQuestions) {
    const { studentSessions } = assignment;
    isStudentResponseAvailable = !!(studentSessions || []).find(
      ({ userId, scoringCompletedAt }) =>
        userId === studentId && !!scoringCompletedAt,
    );
  }

  const normalizedTabs = tabs.map((tab) => ({
    key: tab.key,
    name: tab.label,
    isActive: ppcPartId
      ? parseInt(ppcPartId, 10) === parseInt(tab.id, 10)
      : pathname.includes(tab.name),
    onClick: () => {
      const pathname = tab.id
        ? `${studentContextPath}parts/${tab.id}`
        : `${studentContextPath}${tab.name}`;
      router.push(pathname);
    },
  }));

  const fallBackTab = [
    {
      key: 'questions',
      name: 'Questions',
      isActive: pathname.includes('questions'),
      onClick: () => router.push(`${studentContextPath}questions`),
    },
  ];

  const masteryTab = {
    key: 'csperformance',
    name: 'Content & Skills Performance',
    isActive: pathname.includes('csperformance'),
    onClick: () => router.push(`${studentContextPath}csperformance`),
  };

  const availableTabs = tabs.length === 0 ? fallBackTab : normalizedTabs;
  if (masteryReportFlag) {
    availableTabs.push(masteryTab);
  }
  if (pathname.endsWith('resources')) {
    if (availableTabs.length <= 2) {
      router.replace(`${studentContextPath}questions`);
    }
  }
  const onButtonClick = () => {
    let routePath = `${studentContextPath}responses/questions`;
    let idSet = false;
    if (questionId) {
      idSet = true;
      routePath = `${studentContextPath}responses/questions/${questionId}`;
    }
    if (partId) {
      idSet = true;
      routePath = `${studentContextPath}responses/questions/${questionId}/parts/${partId}`;
    }
    if (!idSet) {
      routePath = `${studentContextPath}responses/questions/${questions[0].id}`;
    }
    router.push(routePath);
  };

  useEffect(() => {
    let route = '';
    if (pathname.endsWith('resources')) {
      route = `/${subject}/assessments/results/${assignmentId}/resources`;
      if (availableTabs.length === 1) {
        route = `/${subject}/assessments/results/${assignmentId}/questions`;
      }
    }
    dispatch({
      type: ACTIONS.updateResultsOverviewRoute,
      payload: route,
    });
  }, []);

  return (
    <ResultsWrapper
      {...resultsData}
      studentContext={{
        hasContext: !!studentId,
        studentId,
        resultsPath,
        questionsPath,
      }}
    >
      <StudentResultsHeader
        {...resultsData}
        {...props}
        isStudentResponseAvailable={isStudentResponseAvailable}
        assignment={assignment}
        studentScore={studentScore}
        onButtonClick={onButtonClick}
        showPerformanceAndTime={showPerformanceAndTime}
        student={student}
      />
      <Feedback assignment={assignment} studentId={studentId} />
      <HorizontalTabs
        tabs={availableTabs}
        direction="horizontal"
        accessibility={{
          ariaLabel: 'Student Results Tabs',
        }}
        classNames={{
          tab: 'flex flex-1 justify-center px-5 py-8 tab',
        }}
        tabsClassName="justify-around my-12"
      />
      {children &&
        React.cloneElement(children, {
          ...resultsData,
          ...props,
          studentId,
          studentResults: { ...student, id: studentId },
          teacherOtherParts: otherParts,
          itemRouteName: 'SingleStudentPerformanceItem',
          optionRouteName: 'SingleStudentPerformanceItemOption',
        })}
    </ResultsWrapper>
  );
};

const mapStateToProps = ({ featureFlag }) => ({
  showFrqResponsesStudent: featureFlag.show_frq_responses_student,
  showFrqResponses: featureFlag.show_frq_responses,
  masteryReportFlag: featureFlag.mastery_report,
});

export default compose(
  withRouter,
  connect(mapStateToProps),
)(TeacherStudentResultsContainer);
